"use client";
import React, { useRef, useState, useEffect } from "react";
import SmoothScrollbar from "../ui/SmoothScrollbar";
import {
  motion,
  useScroll,
  useTransform,
  MotionValue,
  useMotionValueEvent,
  useSpring,
} from "motion/react";
import Image, { StaticImageData } from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTheme } from "next-themes";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowUpRightIcon,
  Moon02Icon,
  Sun01Icon,
} from "@hugeicons/core-free-icons";
import { Projects } from "@/components/utils/data";
import { ProjectType } from "@/components/utils/types";

const Card = ({
  item,
  index,
  targetScale,
  range,
  isMobile,
}: {
  item: ProjectType;
  index: number;
  targetScale: number;
  range: [number, number];
  isMobile: boolean;
}) => {
  const container = useRef(null);
  const router = useRouter();

  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [isDarkView, setIsDarkView] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted) {
      setIsDarkView(resolvedTheme === "dark");
    }
  }, [resolvedTheme, mounted]);

  const { scrollYProgress } = useScroll({
    target: container,
    offset: ["start end", "start start"],
  });

  // Smooth scroll transformations using useSpring to filter out scroll jitter
  const scaleValue = useTransform(scrollYProgress, [0, 1], [0.85, 1]);
  const rotateXValue = useTransform(scrollYProgress, [0, 1], [25, 0]);

  const scale = useSpring(scaleValue, {
    stiffness: 90,
    damping: 20,
    mass: 0.6,
  });

  const rotateX = useSpring(rotateXValue, {
    stiffness: 90,
    damping: 20,
    mass: 0.6,
  });

  // Calculate a top offset so the cards stack visibly like a deck
  const topOffset = 10 + index * 5; // roughly 40px offset per card

  return (
    <div
      ref={container}
      className={`flex items-center justify-center z-10 w-full py-2 md:py-0 md:h-screen md:sticky md:top-0`}
      style={{ perspective: "1000px" }}
    >
      <motion.div
        style={{
          scale: isMobile ? 1 : scale,
          rotateX: isMobile ? 0 : rotateX,
          top: isMobile ? 0 : `calc(2vh + ${index} * var(--stack-offset))`,
          marginBottom: isMobile
            ? "0px"
            : `calc(${Projects.length - index} * 5px)`,
        }}
        initial={{ opacity: 0, y: isMobile ? 30 : 0 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        viewport={{ once: true, margin: "-100px" }}
        className={`relative flex flex-col w-[95vw] lg:w-[90vw] xl:w-[75vw] h-[75vh] rounded-[30px] md:rounded-[40px] border border-black/10 dark:border-white/20 shadow-2xl overflow-hidden origin-top z-10 [--stack-offset:10px] md:[--stack-offset:20px] ${item.gradient_light}`}
      >
        <div className="flex h-full flex-col md:flex-row p-4 md:p-8 gap-5 lg:gap-10">
          {/* Text Content */}
          <div className="flex flex-col justify-between md:w-[45%] lg:w-[45%] h-full z-10 p-1 md:p-4 order-2 md:order-1">
            <div className="space-y-4 md:space-y-6">
              <motion.div
                initial={{
                  opacity: 0,
                  scale: 0.9,
                }}
                whileInView={{
                  opacity: 1,
                  scale: 1,
                }}
                whileHover={{
                  scale: 1.01,
                }}
                transition={{
                  type: "spring",
                  duration: 3,
                }}
                className="flex items-center justify-between"
              >
                <Link
                  href={item.url}
                  target="_blank"
                  className="font-poppins font-semibold lg:text-xl text-md flex items-center gap-2 text-gray-800 dark:text-white/90 opacity-80 hover:opacity-100 transition-opacity w-fit"
                >
                  {item.name}
                  <span className="text-xs px-2 py-0.5 rounded-full border border-current opacity-60">
                    Live
                  </span>
                </Link>
                <span className="lg:text-6xl md:text-4xl text-2xl font-recoleta font-bold text-black/10 dark:text-white/10 pointer-events-none">
                  0{item.index}
                </span>
              </motion.div>

              <motion.h2
                initial={{
                  opacity: 0,
                  scale: 0.9,
                  y: 50,
                }}
                whileInView={{
                  opacity: 1,
                  scale: 1,
                  y: 0,
                }}
                whileHover={{
                  scale: 1.01,
                }}
                transition={{
                  type: "spring",
                  duration: 3,
                }}
                className="font-recoleta text-slate-900 dark:text-slate-50 tracking-wide text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold leading-tight"
              >
                {item.tag1}
              </motion.h2>

              <motion.p
                initial={{
                  opacity: 0,
                  scale: 0.9,
                  y: 50,
                }}
                whileInView={{
                  opacity: 1,
                  scale: 1,
                  y: 0,
                }}
                whileHover={{
                  scale: 1.01,
                }}
                transition={{
                  type: "spring",
                  duration: 3,
                }}
                className="font-poppins text-sm md:text-lg text-gray-700 dark:text-gray-300"
              >
                {item.tag2}
              </motion.p>

              <div className="flex flex-wrap gap-2 pt-2">
                {item.tech.slice(0, 5).map((tech, idx) => (
                  <motion.span
                    initial={{
                      opacity: 0,
                      scale: 0.9,
                      y: 50,
                    }}
                    whileInView={{
                      opacity: 1,
                      scale: 1,
                      y: 0,
                    }}
                    whileHover={{
                      scale: 1.01,
                    }}
                    transition={{
                      type: "spring",
                      duration: 3,
                      delay: idx * 0.1,
                    }}
                    key={idx}
                    className="px-3 py-1 md:px-4 md:py-2 rounded-full bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 text-xs md:text-sm font-poppins text-black/80 dark:text-white/80"
                  >
                    {tech}
                  </motion.span>
                ))}
                {item.tech.length > 5 && (
                  <motion.span
                    initial={{
                      opacity: 0,
                      scale: 0.9,
                      y: 50,
                    }}
                    whileInView={{
                      opacity: 1,
                      scale: 1,
                      y: 0,
                    }}
                    whileHover={{
                      scale: 1.01,
                    }}
                    transition={{
                      type: "spring",
                      duration: 3,
                      delay: 5 * 0.1,
                    }}
                    className="px-3 py-1 md:px-4 md:py-2 rounded-full bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 text-xs md:text-sm font-poppins text-black/80 dark:text-white/80"
                  >
                    +{item.tech.length - 5} more
                  </motion.span>
                )}
              </div>
            </div>
            <div className="flex flex-row gap-2 pb-4 pt-2">
              <motion.div
                initial={{
                  opacity: 0,
                }}
                whileInView={{
                  opacity: 1,
                }}
                whileTap={{
                  scale: 0.95,
                }}
                whileHover={{
                  scale: 1.01,
                }}
                transition={{
                  type: "spring",
                }}
                className="cursor-pointer group w-fit"
              >
                <Link
                  href={item.href}
                  aria-label={`View ${item.name} project details`}
                >
                  <motion.button
                    initial={{
                      opacity: 0,
                      scale: 0.9,
                      x: -50,
                    }}
                    whileInView={{
                      opacity: 1,
                      scale: 1,
                      x: 0,
                    }}
                    transition={{
                      type: "spring",
                      duration: 3,
                    }}
                    className="flex cursor-pointer items-center gap-2 md:gap-3 px-6 py-2.5 md:px-8 md:py-3 rounded-full bg-gray-900 dark:bg-white text-white dark:text-black text-sm md:text-base font-medium transition-transform group-hover:scale-105 active:scale-95"
                  >
                    View Project
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 12h14" />
                      <path d="m12 5 7 7-7 7" />
                    </svg>
                  </motion.button>
                </Link>
              </motion.div>

              <motion.div
                initial={{
                  opacity: 0,
                }}
                whileInView={{
                  opacity: 1,
                }}
                whileTap={{
                  scale: 0.95,
                }}
                whileHover={{
                  scale: 1.01,
                }}
                transition={{
                  type: "spring",
                }}
                className=" group w-fit cursor-pointer"
              >
                <Link
                  href={item.url}
                  target="_blank"
                  aria-label={`Visit ${item.name} live site`}
                >
                  <motion.button
                    initial={{
                      opacity: 0,
                      scale: 0.9,
                      x: -50,
                    }}
                    whileInView={{
                      opacity: 1,
                      scale: 1,
                      x: 0,
                    }}
                    transition={{
                      type: "spring",
                      duration: 3,
                      delay: 0.2,
                    }}
                    className="flex items-center cursor-pointer gap-2 md:gap-3 px-6 py-2.5 md:px-8 md:py-3 rounded-full bg-gray-900 dark:bg-white text-white dark:text-black text-sm md:text-base font-medium transition-transform group-hover:scale-105 active:scale-95"
                  >
                    View Live
                    <HugeiconsIcon
                      icon={ArrowUpRightIcon}
                      className="w-5 h-5"
                    />
                  </motion.button>
                </Link>
              </motion.div>
            </div>
          </div>

          {/* Image Container */}
          <motion.div
            className={`relative h-[250px] w-full md:h-full md:w-[55%] lg:w-[55%] p-2 rounded-[20px] md:rounded-[30px] flex justify-center items-center overflow-hidden order-1 md:order-2 group ${item.image_Gradient}`}
            whileHover={{ scale: 0.98 }}
            initial={{
              opacity: 0,
              scale: 0.9,
            }}
            whileInView={{
              opacity: 1,
              scale: 1,
            }}
            transition={{
              type: "spring",
              duration: 4,
            }}
          >
            {item.dark && item.imgDark && (
              <div
                className="absolute top-4 right-4 z-20 p-3 rounded-full bg-linear-to-t from-blue-900/10 to-transparent dark:from-blue-200/10 dark:to-transparent shadow-xs backdrop-blur-xs text-blue-900/60 dark:text-white/60 transition-colors duration-300 cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDarkView(!isDarkView);
                }}
              >
                {isDarkView ? (
                  <HugeiconsIcon
                    icon={Sun01Icon}
                    size={20}
                  />
                ) : (
                  <HugeiconsIcon
                    icon={Moon02Icon}
                    size={20}
                  />
                )}
              </div>
            )}
            <Link
              href={item.href}
              className="w-full h-full flex items-center justify-center cursor-pointer"
            >
              <Image
                src={
                  isDarkView && item.dark && item.imgDark
                    ? item.imgDark
                    : item.img
                }
                alt={item.name}
                width={1920}
                height={957}
                className="rounded-3xl select-none drag-none"
                // sizes="(max-width: 768px) 100vw, 50vw"
              />
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default function Project() {
  const container = useRef(null);
  const { scrollYProgress } = useScroll({
    target: container,
    offset: ["start start", "end end"],
  });
  const { resolvedTheme } = useTheme();
  const [activeProject, setActiveProject] = useState<ProjectType | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useMotionValueEvent(scrollYProgress, "change", (latest: number) => {
    // Map scroll progress to project index
    // We safeguard the index to be within bounds
    const index = Math.min(
      Math.max(Math.floor(latest * Projects.length), 0),
      Projects.length - 1,
    );
    setActiveProject(Projects[index]);
  });

  // Default to first project if null
  const currentProject = activeProject || Projects[0];

  const getBackgroundColor = (name: string, theme: string | undefined) => {
    const isDark = theme === "dark";

    // Switch case for Tailwind colors as requested
    switch (name) {
      case "Zari":
      case "Devebsy":
        // Indigo
        return isDark ? "#09081a" : "#f5f3ff";

      case "Axiom":
        // Purple
        return isDark ? "#0e0920" : "#faf5ff";

      case "Nest":
      case "Noor":
        // Sky / Amber-Sky
        return isDark ? "#040d1c" : "#f0f9ff";

      case "Syncnotes":
      case "Newsmint":
        // Blue
        return isDark ? "#040c20" : "#f0f7ff";

      case "FoodFast":
        // Green
        return isDark ? "#02110a" : "#f0fdf4";

      case "Gujarat Explorer":
        // Orange
        return isDark ? "#160905" : "#fff7ed";

      default:
        // Default (Violet fallback)
        return isDark ? "#06091c" : "#f5f3ff";
    }
  };

  const backgroundColor = getBackgroundColor(
    currentProject.name,
    resolvedTheme || "dark",
  );

  return (
    <SmoothScrollbar>
      <section
        className="w-full transition-colors duration-500 ease-in-out relative flex flex-col items-center pt-40 pb-20"
        id="projects"
        style={{ backgroundColor }}
      >
        <div className="sticky font-recoleta z-0 flex justify-center lg:top-12 top-20 font-bold left-0 w-full h-full xl:text-[14rem] lg:text-[12rem] md:text-[10rem] sm:text-[8rem] text-[6rem] bg-clip-text bg-linear-to-b from-black/10 via-black/5 dark:from-white/10 dark:via-white/5 to-transparent text-transparent ">
          Projects
        </div>

        <div
          ref={container}
          className="w-full relative px-4 flex flex-col items-center"
        >
          {Projects.map((project, i) => {
            const targetScale = 1 - (Projects.length - i) * 0.05;
            return (
              <Card
                key={i}
                item={project}
                index={i}
                range={[i * 0.1, 1]}
                targetScale={targetScale}
                isMobile={isMobile}
              />
            );
          })}
        </div>
      </section>
    </SmoothScrollbar>
  );
}
